/**
 * "Resulting lines" for step 3 (f10.6, design D7): the counts the server
 * computed on this read and the first lines with their twelve default
 * headcounts and fixed-service total. Nothing here recomputes a figure.
 */

import type { ReactNode } from 'react';
import { AlertTriangle } from 'lucide-react';

import type { KimbleFigures } from '@/api/client';

import { MONTH_LABELS, compactDecimal } from './kimbleDumpView';

function Stat({ label, value, foot }: { label: string; value: number; foot: ReactNode }) {
  return (
    <div className="stat">
      <div className="stat-label">{label}</div>
      <div className="stat-value">{value.toLocaleString()}</div>
      <div className="stat-foot">{foot}</div>
    </div>
  );
}

export function KimbleDumpLinesCard({ figures, footer }: { figures: KimbleFigures; footer?: ReactNode }) {
  return (
    <div className="card">
      <div className="card-head">
        <h3>Resulting lines</h3>
        <span className="hint">Kimble defaults · updated after every decision</span>
      </div>
      <div className="card-body stack">
        <div className="stat-grid">
          <Stat
            label="Forecast lines"
            value={figures.lines_planned}
            foot={
              <>
                {figures.lines_new} new &middot; {figures.lines_existing} refreshed &middot; {figures.lines_absent}{' '}
                absent
              </>
            }
          />
          <Stat
            label="Elements / keys"
            value={figures.elements}
            foot={`${figures.keys} element × country keys`}
          />
          <Stat
            label="Resource BU unmapped"
            value={figures.unmapped_bu_values}
            foot={`${figures.unmapped_bu_rows} rows · ${figures.blank_bu_rows} rows with no BU`}
          />
          <Stat
            label="Not in the hierarchy"
            value={figures.elements_not_in_hierarchy}
            foot={`${figures.elements_not_in_master_list} not in step 1's master list`}
          />
        </div>

        {figures.per_unit.length > 0 && (
          <div className="row btn-row">
            {figures.per_unit.map((unit) => (
              <span key={unit.business_unit.id} className="badge pill-neutral">
                {unit.business_unit.name} · {unit.lines} lines
              </span>
            ))}
          </div>
        )}

        {figures.preview.length === 0 ? (
          <p className="hint">No line results from this dump yet.</p>
        ) : (
          <div className="table-scroll">
            <table className="data compact kd-lines">
              <thead>
                <tr>
                  <th>Element key</th>
                  <th>Unit</th>
                  <th>Status</th>
                  {MONTH_LABELS.map((month) => (
                    <th key={month} className="num">
                      {month}
                    </th>
                  ))}
                  <th className="num">FS total</th>
                </tr>
              </thead>
              <tbody>
                {figures.preview.map((line) => (
                  <tr key={line.element_key} className={line.resource_bu_unmapped ? 'kd-row-unmapped' : ''}>
                    <td className="mono">
                      {line.element_key}
                      {line.resource_bu_unmapped && (
                        <span className="badge pill-warning kd-flag" title="Resource BU unmapped">
                          <AlertTriangle aria-hidden="true" /> BU
                        </span>
                      )}
                    </td>
                    <td>{line.business_unit}</td>
                    <td>{line.status ?? '—'}</td>
                    {line.headcount.map((value, index) => (
                      <td key={MONTH_LABELS[index]} className="num">
                        {compactDecimal(value)}
                      </td>
                    ))}
                    <td className="num">
                      {compactDecimal(line.fixed_service_total)} <span className="hint">{line.currency_code}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {figures.lines_planned > figures.preview.length && (
          <p className="hint">
            Showing the first {figures.preview.length} of {figures.lines_planned.toLocaleString()} lines, in key order.
          </p>
        )}
      </div>
      {footer}
    </div>
  );
}
